import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query'
import { Page } from '@progress/kendo-react-dropdowns'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import {
  CountryComplianceDto,
  CountryComplianceDtoKey,
  CountryComplianceFilterKey,
  CountryComplianceTableDto,
} from 'api/Clients/Types/CountryComplianceDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { FilterCell } from 'components/Filter/FilterCell'
import { Loading } from 'components/Loading'
import MoreButton from 'components/MoreButton/MoreButton'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { calculatePageIndex } from 'features/DataGrid/helpers'
import { useAppDispatch, useAppSelector } from 'store'
import {
  getCountryComplianceInternalRiskLevelAction,
  getCountryComplianceMRLOAnalysesAction,
} from 'store/compliance/compliance.actions'
import { pageableSettings } from 'store/grid/grid.slice'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOneWithFetchingUpdate } from 'utils/api/useFetchOneWithFetchingUpdate'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, isFilterDescriptor } from 'utils/QueryHelpers'

import ClientAreaAuditLogModal from '../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { AddEditCountryRiskValues, EditCountryRiskForm } from './AddEditCountryRiskForm'
import { countryRiskColumns } from './columns'

const CountryRiskPage = (): JSX.Element => {
  const setToast = useContext(ToastContext)

  const dispatch = useAppDispatch()

  const [auth] = useContext(AuthSessionContext)
  const clientsApiClient = useApiClient(ClientsApiClient)
  const mrloAnalyses = useAppSelector((state) => state.compliance.mrlo)
  const internalRiskLevel = useAppSelector((state) => state.compliance.internalRiskLevel)

  const permissions = auth?.permissions || []

  const [countriesData, setCountries] = useState<CountryComplianceTableDto[]>([])
  const [auditLogModal, setAuditLogModal] = useState<{
    visible: boolean
    data?: CountryComplianceDto
  }>({
    visible: false,
  })
  const [paginationState, setPaginationState] = useState<Page>(getPaginationInitialState())
  const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'and', filters: [] })
  const [countryRiskModal, setCountryRiskModal] = useState<{
    data?: CountryComplianceDto
    visible: boolean
  }>({
    visible: false,
  })

  const { callback, forceRefresh } = useCallbackWithForceRefresh(() => {
    const searchQuery = getQuery()
    return clientsApiClient.getCountryCompliance({
      search: searchQuery,
      pageIndex: calculatePageIndex(paginationState.skip, paginationState.take),
      pageSize: paginationState.take,
    })
  }, [])
  const { data, isLoading } = useFetchOneWithFetchingUpdate(callback)

  useEffect(() => {
    if (data) {
      setCountries(prepareData(data.items))
    }
    if (isEmpty(mrloAnalyses)) {
      dispatch(getCountryComplianceMRLOAnalysesAction(clientsApiClient))
    }
    if (isEmpty(internalRiskLevel)) {
      dispatch(getCountryComplianceInternalRiskLevelAction(clientsApiClient))
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!isLoading) {
        setPaginationState((page) => ({ ...page, skip: 0 }))
        forceRefresh()
      }
    }, 500)
    return () => clearTimeout(timeoutId)
  }, [filter.filters]) // eslint-disable-line react-hooks/exhaustive-deps

  const getQuery = useCallback(() => {
    return buildQuery(normalizeCountryRiskSearchQuery(filter)) ?? null
  }, [filter])

  const handleEdit = (countryRisk?: CountryComplianceTableDto) => {
    const originalDTO = (data?.items || []).find((x) => x.id === countryRisk?.id)
    if (originalDTO) setCountryRiskModal({ data: originalDTO, visible: true })
  }

  const setAuditLog = (countryRisk: CountryComplianceTableDto) => {
    const originalDTO = (data?.items || []).find((x) => x.id === countryRisk?.id)
    if (originalDTO) setAuditLogModal({ data: originalDTO, visible: true })
  }

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPaginationState(event.page)
    forceRefresh()
  }

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(event.filter)
  }

  const onSubmit = async (values: AddEditCountryRiskValues) => {
    await clientsApiClient.updateCountryCompliance(values.id, values)
    setToast(successToast('Country Risk updated.'))
    forceRefresh()
    setCountryRiskModal({ visible: false })
  }

  return (
    <React.Fragment>
      {auditLogModal.visible && auditLogModal.data && (
        <ClientAreaAuditLogModal
          isVisible={auditLogModal.visible}
          onClose={() => setAuditLogModal({ visible: false })}
          countryRiskId={auditLogModal.data.id}
        />
      )}

      {countryRiskModal.visible && countryRiskModal.data && (
        <EditCountryRiskForm
          isOpen={countryRiskModal.visible}
          data={countryRiskModal.data}
          onClose={() => setCountryRiskModal({ visible: false })}
          onSubmit={onSubmit}
          mrloAnalyses={mrloAnalyses}
          internalRiskLevel={internalRiskLevel}
        />
      )}
      <Breadcrumbs crumbs={[{ title: 'Country Risk', link: links.complianceCountryRisk.url }]} />
      <Loading isLoading={isLoading}>
        <Grid
          filterable
          resizable
          filter={filter}
          data={countriesData}
          skip={paginationState.skip}
          take={paginationState.take}
          total={data?.totalResults || 0}
          onPageChange={handlePageChange}
          onFilterChange={handleFilterChange}
          sortable={{
            allowUnsort: true,
            mode: 'multiple',
          }}
          pageable={pageableSettings}
          style={{ zIndex: 0 }}
        >
          {countryRiskColumns
            .filter((c) => c.show !== false)
            .map((column, idx) => (
              <Column key={idx} {...column} />
            ))}
          <Column
            width={50}
            title=''
            filterCell={FilterCell}
            cell={(gridCellProps: GridCellProps) => (
              <td>
                <MoreButton
                  items={[
                    {
                      name: 'Edit',
                      'data-test': 'edit',
                      disabled: !hasPermission(
                        permissions,
                        PermissionsEnum.CanEditComplianceCountryRisk
                      ),
                      onClick: () => handleEdit(gridCellProps.dataItem),
                    },
                    {
                      name: 'Audit Log',
                      onClick: () => setAuditLog(gridCellProps.dataItem),
                    },
                  ]}
                  data-test='more-button'
                />
              </td>
            )}
          />
        </Grid>
      </Loading>
    </React.Fragment>
  )
}

const prepareData = (result: CountryComplianceDto[]): CountryComplianceTableDto[] => {
  return (result || []).reduce<CountryComplianceTableDto[]>((previousValue, currentValue) => {
    const transformedDto: CountryComplianceTableDto = {
      id: currentValue.id,
      comment: currentValue.comment,
      countryComplianceMrloAnalyses: currentValue.countryComplianceMrloAnalyses
        .map((x) => x.name)
        .toString(),
      externalRiskLevel: currentValue.externalRiskLevel?.name || null,
      internalRiskLevel: currentValue.internalRiskLevel?.name || null,
      country: currentValue.country.name,
      manualScoring: currentValue.manualScoring,
      score: currentValue.score,
      tickmillCompany: currentValue.tickmillCompany.name,
    }
    return [...previousValue, transformedDto]
  }, [])
}

const normalizeCountryRiskSearchQuery = (
  filterDescriptor: FilterDescriptor[] | CompositeFilterDescriptor
) => {
  const gridFilterDescriptor = filterDescriptor as CompositeFilterDescriptor
  const filters = gridFilterDescriptor.filters
    .filter((filterItem: FilterDescriptor | CompositeFilterDescriptor) => {
      if (isFilterDescriptor(filterItem)) {
        return !!filterItem.value
      }
      return false
    })
    .map((x: FilterDescriptor | CompositeFilterDescriptor) => {
      const filterItem = x as FilterDescriptor

      if (filterItem.field === CountryComplianceDtoKey.tickmillCompany) {
        return {
          ...filterItem,
          field: CountryComplianceFilterKey.tickmillCompany,
          value: filterItem?.value,
        }
      }

      if (filterItem.field === CountryComplianceDtoKey.country) {
        return {
          ...filterItem,
          field: CountryComplianceFilterKey.country,
          value: filterItem?.value,
        }
      }

      if (filterItem.field === CountryComplianceDtoKey.score) {
        return {
          ...filterItem,
          field: CountryComplianceFilterKey.score,
          value: filterItem?.value,
        }
      }

      if (filterItem.field === CountryComplianceDtoKey.externalRiskLevel) {
        return {
          ...filterItem,
          field: CountryComplianceFilterKey.externalRiskLevel,
          value: filterItem?.value,
        }
      }

      if (filterItem.field === CountryComplianceDtoKey.internalRiskLevel) {
        return {
          ...filterItem,
          field: CountryComplianceFilterKey.internalRiskLevel,
          value: filterItem?.value,
        }
      }

      if (filterItem.field === CountryComplianceDtoKey.mRLOAnalysis) {
        return {
          ...filterItem,
          field: CountryComplianceFilterKey.mRLOAnalysis,
          value: filterItem?.value,
        }
      }

      if (filterItem.field === CountryComplianceDtoKey.comment) {
        return {
          ...filterItem,
          field: CountryComplianceFilterKey.comment,
          value: filterItem?.value,
        }
      }

      return filterItem
    })

  return { ...gridFilterDescriptor, filters }
}

export default CountryRiskPage
